import Auth from '@aws-amplify/auth';

import awsmobile from '../aws-exports';

// A hand-rolled client for the AppSync Events WebSocket protocol.
//
// Amplify gained an Events API client in v6; this app is on v3, and the
// upgrade is a much larger change than the protocol is - a handshake, four
// message types and a keep-alive timer. The protocol is documented at
// https://docs.aws.amazon.com/appsync/latest/eventapi/event-api-websocket-protocol.html
//
// One socket is shared by every subscriber. The chat widget and the full-page
// chat can both be mounted at once, and history switching resubscribes on each
// change, so opening a socket per subscription would mean a handshake per
// conversation switch.

const HTTP_DOMAIN = awsmobile.aws_chat_events_http_domain;
const REALTIME_DOMAIN =
    awsmobile.aws_chat_events_realtime_domain ||
    // Both come from CloudFormation, but the realtime host is a fixed
    // transform of the HTTP one, so a missing value is recoverable.
    (HTTP_DOMAIN ? HTTP_DOMAIN.replace('appsync-api', 'appsync-realtime-api') : '');

const CONNECT_TIMEOUT_MS = 10000;
// Backoff for a dropped socket. Jittered, because every open chat tab loses
// the connection at the same instant when the service recycles one.
const RETRY_BASE_MS = 500;
const RETRY_MAX_MS = 10000;

export const isConfigured = () => Boolean(HTTP_DOMAIN && REALTIME_DOMAIN);

const base64UrlEncode = (value) =>
    btoa(JSON.stringify(value)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

// The Cognito ID token, refreshed on every use. currentSession() renews it
// when it is close to expiry, which matters here: a socket can outlive a
// token, and a resubscribe after a reconnect must not present a stale one.
const authHeaders = async () => {
    const session = await Auth.currentSession();
    return {
        host: HTTP_DOMAIN,
        Authorization: session.getIdToken().getJwtToken()
    };
};

const makeId = () => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// channel -> { id, handlers:Set, ready:boolean }
const subscriptions = new Map();

let socket = null;
let connecting = null;
// The handshake is not finished when the socket opens - it is finished when
// connection_ack arrives. Nothing may subscribe before then, so both the flag
// and the waiting caller are tracked here.
let acked = false;
let pendingAck = null;
let keepAliveTimer = null;
let retries = 0;

const settleAck = (method, value) => {
    if (!pendingAck) {
        return;
    }
    const waiter = pendingAck;
    pendingAck = null;
    waiter[method](value);
};

const closeSocket = () => {
    if (keepAliveTimer) {
        clearTimeout(keepAliveTimer);
        keepAliveTimer = null;
    }
    if (socket) {
        const dying = socket;
        socket = null;
        acked = false;
        try {
            dying.onclose = null;
            dying.close();
        } catch (error) {
            // Already gone.
        }
    }
};

const armKeepAlive = (timeoutMs) => {
    if (keepAliveTimer) {
        clearTimeout(keepAliveTimer);
    }
    // The service sends "ka" every 60s and expects the client to give up if one
    // does not arrive. Without this a half-open socket looks healthy for ever
    // and the answer simply never appears.
    keepAliveTimer = setTimeout(() => {
        closeSocket();
        scheduleReconnect();
    }, timeoutMs);
};

const scheduleReconnect = () => {
    if (!subscriptions.size) {
        return;
    }
    const delay = Math.min(RETRY_BASE_MS * 2 ** retries, RETRY_MAX_MS);
    retries += 1;
    setTimeout(() => {
        connect().catch(() => {
            /* connect() reschedules itself */
        });
    }, delay * (0.5 + Math.random() / 2));
};

const sendSubscribe = async (channel, entry) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        return;
    }
    socket.send(
        JSON.stringify({
            type: 'subscribe',
            id: entry.id,
            channel,
            authorization: await authHeaders()
        })
    );
};

const handleMessage = (raw) => {
    let message;
    try {
        message = JSON.parse(raw);
    } catch (error) {
        return;
    }

    switch (message.type) {
        case 'connection_ack':
            retries = 0;
            acked = true;
            armKeepAlive(message.connectionTimeoutMs || 300000);
            // Everything already registered is subscribed from here. On a
            // reconnect those are live subscriptions that lost their socket; on
            // a first connect it is the caller waiting inside subscribe(). That
            // caller must not also send its own frame - see subscribe().
            subscriptions.forEach((entry, channel) => {
                entry.ready = false;
                sendSubscribe(channel, entry);
            });
            settleAck('resolve');
            break;

        case 'ka':
            armKeepAlive(300000);
            break;

        case 'subscribe_success': {
            const entry = [...subscriptions.values()].find((value) => value.id === message.id);
            if (entry) {
                entry.ready = true;
                entry.resolve();
            }
            break;
        }

        case 'subscribe_error': {
            const entry = [...subscriptions.values()].find((value) => value.id === message.id);
            if (entry) {
                entry.reject(new Error(message.errors?.[0]?.message || 'Subscription refused'));
            }
            break;
        }

        case 'data': {
            const entry = [...subscriptions.values()].find((value) => value.id === message.id);
            if (!entry) {
                return;
            }
            // The service has shipped both a single stringified event and an
            // array of them; accept either rather than guess.
            const payloads = Array.isArray(message.event) ? message.event : [message.event];
            payloads.forEach((payload) => {
                let parsed;
                try {
                    parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
                } catch (error) {
                    return;
                }
                entry.handlers.forEach((handler) => handler(parsed));
            });
            break;
        }

        default:
            break;
    }
};

const connect = () => {
    // Open is not enough: a socket still mid-handshake cannot carry a subscribe.
    if (socket && socket.readyState === WebSocket.OPEN && acked) {
        return Promise.resolve(socket);
    }
    if (connecting) {
        return connecting;
    }

    connecting = (async () => {
        const authorization = await authHeaders();
        const protocols = ['aws-appsync-event-ws', `header-${base64UrlEncode(authorization)}`];
        const next = new WebSocket(`wss://${REALTIME_DOMAIN}/event/realtime`, protocols);

        // Handlers go on before connection_init is sent. connection_ack can
        // arrive in the same tick as the open, and a socket with no onmessage
        // yet drops it silently - the handshake then times out for no visible
        // reason.
        next.onmessage = (event) => handleMessage(event.data);
        next.onclose = () => {
            if (socket === next) {
                socket = null;
                acked = false;
                scheduleReconnect();
            }
            settleAck('reject', new Error('The chat stream closed during the handshake.'));
        };

        // The connection_ack handler calls sendSubscribe, which reads `socket`,
        // so this has to be in place before the ack can land.
        socket = next;
        acked = false;

        // Resolve on connection_ack rather than on open. The protocol forbids a
        // subscribe before the ack and AppSync refuses one that arrives early,
        // which surfaced as an intermittent "Subscription refused" on whichever
        // turn happened to find a cached Cognito token.
        try {
            await new Promise((resolve, reject) => {
                const timer = setTimeout(
                    () => reject(new Error('Timed out opening the chat stream')),
                    CONNECT_TIMEOUT_MS
                );
                pendingAck = {
                    resolve: () => {
                        clearTimeout(timer);
                        resolve();
                    },
                    reject: (error) => {
                        clearTimeout(timer);
                        reject(error);
                    }
                };
                next.onopen = () => next.send(JSON.stringify({ type: 'connection_init' }));
                next.onerror = () => settleAck('reject', new Error('Could not open the chat stream'));
            });
        } catch (error) {
            // A socket that never acked is unusable, and `socket` is already
            // pointing at it. Leaving it there would strand the next connect()
            // behind something that can never become ready, and leak the socket.
            pendingAck = null;
            if (socket === next) {
                socket = null;
                acked = false;
            }
            try {
                next.onclose = null;
                next.close();
            } catch (closeError) {
                // Already gone.
            }
            throw error;
        }

        return next;
    })();

    const pending = connecting;
    pending.then(
        () => {
            connecting = null;
        },
        () => {
            connecting = null;
            scheduleReconnect();
        }
    );

    return pending;
};

const SUBSCRIBE_TIMEOUT_MS = 10000;

/**
 * Listen to one channel. Resolves once the subscription is live - await it
 * before triggering whatever publishes, or the opening events are lost. The
 * Events API has no replay.
 *
 * Returns a function that removes this listener and, when it was the last one
 * on the channel, unsubscribes.
 */
export const subscribe = async (channel, handler) => {
    if (!isConfigured()) {
        throw new Error('No chat stream endpoint is configured for this environment.');
    }

    let entry = subscriptions.get(channel);

    if (!entry) {
        entry = { id: makeId(), handlers: new Set(), ready: false };
        entry.settled = new Promise((resolve, reject) => {
            // A subscribe that is never acknowledged - a socket that closed
            // between connect and send, most often - would otherwise leave the
            // caller awaiting for ever, and the send button disabled with it.
            const timer = setTimeout(
                () => reject(new Error('The chat stream did not acknowledge the subscription.')),
                SUBSCRIBE_TIMEOUT_MS
            );
            entry.reject = (error) => {
                clearTimeout(timer);
                reject(error);
            };
            entry.resolve = () => {
                clearTimeout(timer);
                resolve();
            };
        });
        // Nothing may be awaiting `settled` when a late subscribe_error lands,
        // and an unobserved rejection is a console error in every browser.
        entry.settled.catch(() => {});

        // A socket that is already past its handshake will not produce another
        // connection_ack, so this subscribe has to be sent here. One that still
        // has to be opened sends it from the ack handler instead. Doing both
        // races the handshake, and a duplicate id on one socket is an error in
        // its own right. Checked before the entry is registered and before any
        // await, so the ack handler cannot see it and reach the same conclusion.
        const live = Boolean(socket && socket.readyState === WebSocket.OPEN && acked);
        subscriptions.set(channel, entry);

        try {
            await connect();
            if (live) {
                await sendSubscribe(channel, entry);
            }
            await entry.settled;
        } catch (error) {
            subscriptions.delete(channel);
            throw error;
        }
    } else if (!entry.ready) {
        await entry.settled;
    }

    entry.handlers.add(handler);

    return () => {
        const current = subscriptions.get(channel);
        if (!current) {
            return;
        }
        current.handlers.delete(handler);
        if (current.handlers.size) {
            return;
        }

        subscriptions.delete(channel);
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'unsubscribe', id: current.id }));
        }
        if (!subscriptions.size) {
            closeSocket();
        }
    };
};
